import type { TestLandingConfig } from "./config.js";

/**
 * The 61 generated standardized-test landing configs (national + per-state).
 * Derived from the project's generated-test dataset; kept in a separate file
 * from the 4 bespoke configs so the hand-edited `configs.ts` stays short.
 * `configs.ts` concatenates this array after the bespoke four into the single
 * `TEST_LANDING_CONFIGS` that the route, prerender, and tests all read.
 */
export const GENERATED_TEST_LANDING_CONFIGS: TestLandingConfig[] = [
  {
    slug: "psat-nmsqt",
    fullName: "PSAT/NMSQT (Preliminary SAT / National Merit Scholarship Qualifying Test)",
    shortName: "PSAT/NMSQT",
    scope: "national",
    seo: {
      title: "PSAT/NMSQT Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the PSAT/NMSQT (Preliminary SAT / National Merit Scholarship Qualifying Test) math test with fun, adaptive games. Grades 10-11.…",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the PSAT/NMSQT?",
    heroSubhead: "The PSAT/NMSQT (Preliminary SAT / National Merit Scholarship Qualifying Test) measures math skills for grades 10-11. Sports Academy Games turns PSAT/NMSQT math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 10-11",
    heroMeta: "Grades 10-11 · College Board",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the PSAT/NMSQT",
      lede: "PSAT/NMSQT math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Algebra",
            "Advanced Math",
            "Problem-Solving & Data Analysis",
            "Geometry & Trigonometry",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn PSAT/NMSQT prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact PSAT/NMSQT math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the PSAT/NMSQT",
      cards: [
        {
          title: "When it's given",
          body: "Fall (October)",
        },
        {
          title: "Adaptive test",
          body: "Yes (digital adaptive modules). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "National Merit gateway math test for 10th–11th graders",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "PSAT/NMSQT",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://satsuite.collegeboard.org/psat-nmsqt",
  },
  {
    slug: "psat-10",
    fullName: "PSAT 10",
    shortName: "PSAT 10",
    scope: "national",
    seo: {
      title: "PSAT 10 Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the PSAT 10 math test with fun, adaptive games. Grade 10. Start with a free diagnostic.",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the PSAT 10?",
    heroSubhead: "The PSAT 10 measures math skills for grade 10. Sports Academy Games turns PSAT 10 math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grade 10",
    heroMeta: "Grade 10 · College Board",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the PSAT 10",
      lede: "PSAT 10 math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Algebra",
            "Advanced Math",
            "Problem-Solving & Data Analysis",
            "Geometry & Trigonometry",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn PSAT 10 prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact PSAT 10 math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the PSAT 10",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (digital adaptive modules). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Spring college-readiness math check for 10th graders",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "PSAT 10",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://satsuite.collegeboard.org/psat-10",
  },
  {
    slug: "psat-89",
    fullName: "PSAT 8/9",
    shortName: "PSAT 8/9",
    scope: "national",
    seo: {
      title: "PSAT 8/9 Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the PSAT 8/9 math test with fun, adaptive games. Grades 8-9. Start with a free diagnostic.",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the PSAT 8/9?",
    heroSubhead: "The PSAT 8/9 measures math skills for grades 8-9. Sports Academy Games turns PSAT 8/9 math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 8-9",
    heroMeta: "Grades 8-9 · College Board",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the PSAT 8/9",
      lede: "PSAT 8/9 math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Algebra",
            "Problem-Solving & Data Analysis",
            "Advanced Math",
            "Geometry",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn PSAT 8/9 prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact PSAT 8/9 math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the PSAT 8/9",
      cards: [
        {
          title: "When it's given",
          body: "Fall or Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (digital adaptive modules). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Earliest SAT Suite math entry point for 8th–9th graders",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "PSAT 8/9",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://satsuite.collegeboard.org/psat-8-9",
  },
  {
    slug: "act",
    fullName: "ACT (American College Testing)",
    shortName: "ACT",
    scope: "national",
    seo: {
      title: "ACT Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the ACT (American College Testing) math test with fun, adaptive games. Grade 11 (typical); open to 8-12. Start with a free…",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the ACT?",
    heroSubhead: "The ACT (American College Testing) measures math skills for grade 11 (typical); open to 8-12. Sports Academy Games turns ACT math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grade 11 (typical); open to 8-12",
    heroMeta: "Grade 11 (typical); open to 8-12 · ACT Inc.",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the ACT",
      lede: "ACT math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Pre-Algebra/Elem. Algebra",
            "Intermediate Algebra/Coordinate Geometry",
            "Plane Geometry",
            "Trigonometry",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn ACT prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact ACT math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the ACT",
      cards: [
        {
          title: "When it's given",
          body: "Spring or Fall (test centers); state school-day varies",
        },
        {
          title: "Adaptive test",
          body: "No (fixed form). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Top college-admissions competitor to SAT with dedicated math section",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "ACT",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.act.org/content/act/en/products-and-services/the-act/test-preparation.html",
  },
  {
    slug: "act-aspire",
    fullName: "ACT Aspire Summative",
    shortName: "ACT Aspire",
    scope: "national",
    seo: {
      title: "ACT Aspire Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the ACT Aspire Summative math test with fun, adaptive games. Grades 3-10. Start with a free diagnostic.",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the ACT Aspire?",
    heroSubhead: "The ACT Aspire Summative measures math skills for grades 3-10. Sports Academy Games turns ACT Aspire math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-10",
    heroMeta: "Grades 3-10 · ACT Inc.",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the ACT Aspire",
      lede: "ACT Aspire math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebra",
            "Functions",
            "Geometry",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn ACT Aspire prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact ACT Aspire math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the ACT Aspire",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "No (fixed form). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "K-10 longitudinal math benchmark aligned to ACT college readiness pathway",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "ACT Aspire",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.act.org/content/act/en/products-and-services/act-aspire.html",
  },
  {
    slug: "star-math",
    fullName: "Star Math (Renaissance)",
    shortName: "Star Math",
    scope: "national",
    seo: {
      title: "Star Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the Star Math (Renaissance) math test with fun, adaptive games. Grades 1-12. Start with a free diagnostic.",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the Star Math?",
    heroSubhead: "The Star Math (Renaissance) measures math skills for grades 1-12. Sports Academy Games turns Star Math math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 1-12",
    heroMeta: "Grades 1-12 · Renaissance",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the Star Math",
      lede: "Star Math math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Numbers & Operations",
            "Algebra",
            "Geometry",
            "Measurement & Data",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn Star Math prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact Star Math math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the Star Math",
      cards: [
        {
          title: "When it's given",
          body: "Fall / Winter / Spring (flexible)",
        },
        {
          title: "Adaptive test",
          body: "Yes. Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Fast 20-minute adaptive screener used for universal screening and progress monitoring",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "Star Math",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.renaissance.com/products/star-math/",
  },
  {
    slug: "sbac",
    fullName: "Smarter Balanced Assessments (SBAC)",
    shortName: "SBAC",
    scope: "national",
    seo: {
      title: "SBAC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the SBAC (Smarter Balanced Assessments (SBAC)) math test with fun, adaptive games. Grades 3-8 + 11. Start with a free diagnostic.",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the SBAC?",
    heroSubhead: "The Smarter Balanced Assessments (SBAC) measures math skills for grades 3-8 + 11. Sports Academy Games turns SBAC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + 11",
    heroMeta: "Grades 3-8 + 11 · Smarter Balanced Assessment Consortium",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the SBAC",
      lede: "SBAC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn SBAC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact SBAC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the SBAC",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Consortium summative used by ~12 states; rigorous CCSS math adaptive test",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "SBAC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://smarterbalanced.org/",
  },
  {
    slug: "naep",
    fullName: "National Assessment of Educational Progress",
    shortName: "NAEP",
    scope: "national",
    seo: {
      title: "NAEP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the NAEP (National Assessment of Educational Progress) math test with fun, adaptive games. Grades 4 / 8 / 12. Start with a free…",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the NAEP?",
    heroSubhead: "The National Assessment of Educational Progress measures math skills for grades 4 / 8 / 12. Sports Academy Games turns NAEP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 4 / 8 / 12",
    heroMeta: "Grades 4 / 8 / 12 · NCES / U.S. Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the NAEP",
      lede: "NAEP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number Properties & Operations",
            "Measurement",
            "Geometry",
            "Data Analysis & Probability",
            "Algebra",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn NAEP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact NAEP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the NAEP",
      cards: [
        {
          title: "When it's given",
          body: "Biennial (winter)",
        },
        {
          title: "Adaptive test",
          body: "No (matrix sampled). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Nation's Report Card — gold standard for comparing state math performance",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "NAEP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://nces.ed.gov/nationsreportcard/",
  },
  {
    slug: "isip-math",
    fullName: "ISIP Math (Istation's Indicators of Progress)",
    shortName: "ISIP Math",
    scope: "national",
    seo: {
      title: "ISIP Math Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the ISIP Math (Istation's Indicators of Progress) math test with fun, adaptive games. Grades PreK-8. Start with a free diagnostic.",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the ISIP Math?",
    heroSubhead: "The ISIP Math (Istation's Indicators of Progress) measures math skills for grades prek-8. Sports Academy Games turns ISIP Math math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades PreK-8",
    heroMeta: "Grades PreK-8 · Istation",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the ISIP Math",
      lede: "ISIP Math math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number Sense",
            "Computation & Algebraic Thinking",
            "Geometry & Measurement",
            "Statistics",
            "Financial Literacy",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn ISIP Math prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact ISIP Math math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the ISIP Math",
      cards: [
        {
          title: "When it's given",
          body: "Monthly or 3× per year (flexible)",
        },
        {
          title: "Adaptive test",
          body: "Yes. Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Monthly adaptive math screener used in TX and other states for early intervention",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "ISIP Math",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.istation.com/Solutions/Math",
  },
  {
    slug: "parcc",
    fullName: "PARCC (Partnership for Assessment of Readiness for College and Careers)",
    shortName: "PARCC",
    scope: "national",
    seo: {
      title: "PARCC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the PARCC (Partnership for Assessment of Readiness for College and Careers) math test with fun, adaptive games. Grades 3-8 + HS…",
    },
    heroTag: "National Test · Math Prep",
    heroHeadline: "Does your child need help preparing for the PARCC?",
    heroSubhead: "The PARCC (Partnership for Assessment of Readiness for College and Careers) measures math skills for grades 3-8 + hs (where still active). Sports Academy Games turns PARCC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + HS (where still active)",
    heroMeta: "Grades 3-8 + HS (where still active) · Pearson / state partners",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the PARCC",
      lede: "PARCC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Expressions & Equations",
            "Geometry",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn PARCC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact PARCC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the PARCC",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Consortium test now largely superseded; still active in DC and NJ-adjacent programs",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "PARCC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://parcc-assessment.org/",
  },
  {
    slug: "iar",
    fullName: "Illinois Assessment of Readiness",
    shortName: "IAR",
    scope: "Illinois",
    state: "Illinois",
    seo: {
      title: "IAR Test Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the IAR (Illinois Assessment of Readiness) math test with fun, adaptive games. Grades 3-8. Start with a free diagnostic.",
    },
    heroTag: "Illinois · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the IAR?",
    heroSubhead: "The Illinois Assessment of Readiness measures math skills for grades 3-8. Sports Academy Games turns IAR math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8",
    heroMeta: "Grades 3-8 · Pearson / Illinois",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the IAR",
      lede: "IAR math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Expressions & Equations",
            "Geometry",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn IAR prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact IAR math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the IAR",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–April)",
        },
        {
          title: "Why it matters",
          body: "PARCC-lineage test used by Illinois; rigorous multi-step reasoning math",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "IAR",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.isbe.net/iar",
  },
  {
    slug: "acap",
    fullName: "Alabama Comprehensive Assessment Program (ACAP Summative)",
    shortName: "ACAP",
    scope: "Alabama",
    state: "Alabama",
    seo: {
      title: "ACAP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the ACAP (Alabama Comprehensive Assessment Program (ACAP Summative)) math test with fun, adaptive games. Grades 2-8 math (+…",
    },
    heroTag: "Alabama · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the ACAP?",
    heroSubhead: "The Alabama Comprehensive Assessment Program (ACAP Summative) is Alabama's math assessment for grades 2-8 math (+ algebra i eoc). Sports Academy Games turns ACAP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 2-8 math (+ Algebra I EOC)",
    heroMeta: "Grades 2-8 math (+ Algebra I EOC) · Cognia / Alabama Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the ACAP",
      lede: "ACAP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations in Base 10",
            "Fractions",
            "Geometry",
            "Measurement & Data",
            "Algebraic Reasoning",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn ACAP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact ACAP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the ACAP",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–May)",
        },
        {
          title: "Why it matters",
          body: "Alabama's state math test for grades 2–8 plus Algebra I end-of-course",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "ACAP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.alabamaachieves.org/assessment/",
  },
  {
    slug: "ak-star",
    fullName: "Alaska System of Academic Readiness",
    shortName: "AK STAR",
    scope: "Alaska",
    state: "Alaska",
    seo: {
      title: "AK STAR Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the AK STAR (Alaska System of Academic Readiness) math test with fun, adaptive games. Grades 3-9 (math + ELA); Science 5/8/10.…",
    },
    heroTag: "Alaska · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the AK STAR?",
    heroSubhead: "The Alaska System of Academic Readiness is Alaska's math assessment for grades 3-9 (math + ela); science 5/8/10. Sports Academy Games turns AK STAR math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-9 (math + ELA); Science 5/8/10",
    heroMeta: "Grades 3-9 (math + ELA); Science 5/8/10 · DRC / Alaska Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the AK STAR",
      lede: "AK STAR math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebra",
            "Geometry",
            "Measurement & Data",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn AK STAR prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact AK STAR math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the AK STAR",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Why it matters",
          body: "Annual math exam for all Alaska public students in grades 3–9",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "AK STAR",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://education.alaska.gov/tls/assessment/akstar.html",
  },
  {
    slug: "aasa",
    fullName: "Arizona Academic Standards Assessment",
    shortName: "AASA",
    scope: "Arizona",
    state: "Arizona",
    seo: {
      title: "AASA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the AASA (Arizona Academic Standards Assessment) math test with fun, adaptive games. Grades 3-8 math. Start with a free…",
    },
    heroTag: "Arizona · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the AASA?",
    heroSubhead: "The Arizona Academic Standards Assessment is Arizona's math assessment for grades 3-8 math. Sports Academy Games turns AASA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math",
    heroMeta: "Grades 3-8 math · Pearson / Arizona Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the AASA",
      lede: "AASA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Measurement & Data",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn AASA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact AASA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the AASA",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–April)",
        },
        {
          title: "Why it matters",
          body: "Arizona's primary K-8 math test aligned to Arizona Academic Standards",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "AASA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.azed.gov/assessment/aasa",
  },
  {
    slug: "atlas-ar",
    fullName: "Arkansas Teaching Learning & Assessment System",
    shortName: "ATLAS",
    scope: "Arkansas",
    state: "Arkansas",
    seo: {
      title: "ATLAS Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the ATLAS (Arkansas Teaching Learning & Assessment System) math test with fun, adaptive games. Grades 3-8 + EOC (Algebra…",
    },
    heroTag: "Arkansas · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the ATLAS?",
    heroSubhead: "The Arkansas Teaching Learning & Assessment System is Arkansas's math assessment for grades 3-8 + eoc (algebra i/geometry). Sports Academy Games turns ATLAS math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + EOC (Algebra I/Geometry)",
    heroMeta: "Grades 3-8 + EOC (Algebra I/Geometry) · Pearson / Arkansas Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the ATLAS",
      lede: "ATLAS math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number Sense",
            "Algebraic Reasoning",
            "Geometry & Measurement",
            "Data Analysis",
            "Functions",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn ATLAS prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact ATLAS math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the ATLAS",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Arkansas's 2024-launch replacement for ACT Aspire; covers 3–8 + HS EOC",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "ATLAS",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://dese.ade.arkansas.gov/Offices/public-school-accountability/assessment-test-scores",
  },
  {
    slug: "caaspp",
    fullName: "California Assessment of Student Performance and Progress (Smarter Balanced)",
    shortName: "CAASPP",
    scope: "California",
    state: "California",
    seo: {
      title: "CAASPP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the CAASPP (California Assessment of Student Performance and Progress (Smarter Balanced)) math test with fun, adaptive games.…",
    },
    heroTag: "California · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the CAASPP?",
    heroSubhead: "The California Assessment of Student Performance and Progress (Smarter Balanced) is California's math assessment for grades 3-8 + grade 11. Sports Academy Games turns CAASPP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · California Dept. of Education / ETS",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the CAASPP",
      lede: "CAASPP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn CAASPP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact CAASPP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the CAASPP",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Nation's largest Smarter Balanced deployment — grades 3-8 and 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "CAASPP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.cde.ca.gov/ta/tg/sa/",
  },
  {
    slug: "cmas",
    fullName: "Colorado Measures of Academic Success",
    shortName: "CMAS",
    scope: "Colorado",
    state: "Colorado",
    seo: {
      title: "CMAS Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the CMAS (Colorado Measures of Academic Success) math test with fun, adaptive games. Grades 3-8 math; PSAT/SAT for HS. Start with…",
    },
    heroTag: "Colorado · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the CMAS?",
    heroSubhead: "The Colorado Measures of Academic Success is Colorado's math assessment for grades 3-8 math; psat/sat for hs. Sports Academy Games turns CMAS math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math; PSAT/SAT for HS",
    heroMeta: "Grades 3-8 math; PSAT/SAT for HS · DRC / Colorado Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the CMAS",
      lede: "CMAS math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Algebraic Thinking",
            "Number & Operations",
            "Expressions & Equations",
            "Geometry",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn CMAS prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact CMAS math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the CMAS",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April)",
        },
        {
          title: "Why it matters",
          body: "Colorado's CCSS-aligned K-8 math summative; HS uses PSAT/SAT",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "CMAS",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://ed.cde.state.co.us/assessment/cmas",
  },
  {
    slug: "ct-sbac",
    fullName: "Connecticut Smarter Balanced Assessment",
    shortName: "CT SBAC",
    scope: "Connecticut",
    state: "Connecticut",
    seo: {
      title: "CT SBAC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the CT SBAC (Connecticut Smarter Balanced Assessment) math test with fun, adaptive games. Grades 3-8 + Grade 11. Start with a…",
    },
    heroTag: "Connecticut · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the CT SBAC?",
    heroSubhead: "The Connecticut Smarter Balanced Assessment is Connecticut's math assessment for grades 3-8 + grade 11. Sports Academy Games turns CT SBAC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · Connecticut State Dept. of Education / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the CT SBAC",
      lede: "CT SBAC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn CT SBAC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact CT SBAC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the CT SBAC",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Connecticut uses SBAC as its accountability assessment for grades 3-8 and 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "CT SBAC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://portal.ct.gov/sde/student-assessment/smarter-balanced",
  },
  {
    slug: "dessa",
    fullName: "Delaware Student Success Assessment",
    shortName: "DeSSA",
    scope: "Delaware",
    state: "Delaware",
    seo: {
      title: "DeSSA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the DeSSA (Delaware Student Success Assessment) math test with fun, adaptive games. Grades 3-8 + SAT for HS. Start with a free…",
    },
    heroTag: "Delaware · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the DeSSA?",
    heroSubhead: "The Delaware Student Success Assessment is Delaware's math assessment for grades 3-8 + sat for hs. Sports Academy Games turns DeSSA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + SAT for HS",
    heroMeta: "Grades 3-8 + SAT for HS · Delaware Dept. of Education / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the DeSSA",
      lede: "DeSSA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Measurement & Data",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn DeSSA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact DeSSA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the DeSSA",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Delaware uses Smarter Balanced (DeSSA) for grades 3-8 and SAT for grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "DeSSA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://education.delaware.gov/educators/academic-support/standards-and-assessments/mathematics/de-math-assessments/",
  },
  {
    slug: "dc-cape",
    fullName: "DC Comprehensive Assessment Program",
    shortName: "DC CAPE",
    scope: "District of Columbia",
    state: "District of Columbia",
    seo: {
      title: "DC CAPE Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the DC CAPE (DC Comprehensive Assessment Program) math test with fun, adaptive games. Grades 3-8 + Grade 10 (HS). Start with a…",
    },
    heroTag: "District of Columbia · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the DC CAPE?",
    heroSubhead: "The DC Comprehensive Assessment Program is District of Columbia's math assessment for grades 3-8 + grade 10 (hs). Sports Academy Games turns DC CAPE math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 10 (HS)",
    heroMeta: "Grades 3-8 + Grade 10 (HS) · Pearson / DC OSSE",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the DC CAPE",
      lede: "DC CAPE math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Expressions & Equations",
            "Geometry",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn DC CAPE prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact DC CAPE math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the DC CAPE",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "DC's PARCC-successor math test for grades 3-8 and grade 10",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "DC CAPE",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://osse.dc.gov/dccape",
  },
  {
    slug: "fast",
    fullName: "Florida Assessment of Student Thinking",
    shortName: "FAST",
    scope: "Florida",
    state: "Florida",
    seo: {
      title: "FAST Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the FAST (Florida Assessment of Student Thinking) math test with fun, adaptive games. Grades 3-8 math + EOC (Algebra I/Geometry).…",
    },
    heroTag: "Florida · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the FAST?",
    heroSubhead: "The Florida Assessment of Student Thinking is Florida's math assessment for grades 3-8 math + eoc (algebra i/geometry). Sports Academy Games turns FAST math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math + EOC (Algebra I/Geometry)",
    heroMeta: "Grades 3-8 math + EOC (Algebra I/Geometry) · FDOE / Cambium Assessment",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the FAST",
      lede: "FAST math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number Sense & Operations",
            "Algebraic Reasoning",
            "Functions",
            "Geometric Reasoning",
            "Data Analysis",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn FAST prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact FAST math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the FAST",
      cards: [
        {
          title: "When it's given",
          body: "Three times per year (PM1 fall / PM2 winter / PM3 spring)",
        },
        {
          title: "Why it matters",
          body: "Florida's 3× yearly progress monitoring math test replacing FSA; grades 3-8 + EOC",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "FAST",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.fldoe.org/accountability/assessments/k-12-student-assessment/best/",
  },
  {
    slug: "georgia-milestones",
    fullName: "Georgia Milestones Assessment System",
    shortName: "Georgia Milestones",
    scope: "Georgia",
    state: "Georgia",
    seo: {
      title: "Georgia Milestones Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the Georgia Milestones Assessment System math test with fun, adaptive games. Grades 3-8 EOG + HS EOC (Algebra I / Geometry /…",
    },
    heroTag: "Georgia · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the Georgia Milestones?",
    heroSubhead: "The Georgia Milestones Assessment System is Georgia's math assessment for grades 3-8 eog + hs eoc (algebra i / geometry / analytic geometry). Sports Academy Games turns Georgia Milestones math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 EOG + HS EOC (Algebra I / Geometry / Analytic Geometry)",
    heroMeta: "Grades 3-8 EOG + HS EOC (Algebra I / Geometry / Analytic Geometry) · Cambium Assessment / Georgia DOE",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the Georgia Milestones",
      lede: "Georgia Milestones math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Expressions & Equations",
            "Geometry",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn Georgia Milestones prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact Georgia Milestones math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the Georgia Milestones",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Why it matters",
          body: "Georgia's comprehensive math summative — EOG grades 3-8 plus HS course EOCs",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "Georgia Milestones",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://gadoe.org/assessment-accountability/georgia-milestones/",
  },
  {
    slug: "hi-sba",
    fullName: "Hawaii Smarter Balanced Assessment",
    shortName: "HI SBA",
    scope: "Hawaii",
    state: "Hawaii",
    seo: {
      title: "HI SBA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the HI SBA (Hawaii Smarter Balanced Assessment) math test with fun, adaptive games. Grades 3-8 + Grade 11. Start with a free…",
    },
    heroTag: "Hawaii · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the HI SBA?",
    heroSubhead: "The Hawaii Smarter Balanced Assessment is Hawaii's math assessment for grades 3-8 + grade 11. Sports Academy Games turns HI SBA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · Hawaii DOE / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the HI SBA",
      lede: "HI SBA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn HI SBA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact HI SBA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the HI SBA",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Hawaii uses SBAC for all students in grades 3-8 and grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "HI SBA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://hawaiipublicschools.org/academics/types-of-testing/",
  },
  {
    slug: "isat",
    fullName: "Idaho Standards Achievement Test",
    shortName: "ISAT",
    scope: "Idaho",
    state: "Idaho",
    seo: {
      title: "ISAT Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the ISAT (Idaho Standards Achievement Test) math test with fun, adaptive games. Grades 3-8 + Grade 11. Start with a free…",
    },
    heroTag: "Idaho · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the ISAT?",
    heroSubhead: "The Idaho Standards Achievement Test is Idaho's math assessment for grades 3-8 + grade 11. Sports Academy Games turns ISAT math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · Idaho State Dept. of Education / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the ISAT",
      lede: "ISAT math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Measurement & Data",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn ISAT prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact ISAT math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the ISAT",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Idaho uses Smarter Balanced (branded ISAT) for grades 3-8 and grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "ISAT",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.sde.idaho.gov/about-us/departments/assessment-accountability/idaho-standards-achievement-test-isat/",
  },
  {
    slug: "il-iar",
    fullName: "Illinois Assessment of Readiness",
    shortName: "IAR",
    scope: "Illinois",
    state: "Illinois",
    seo: {
      title: "IAR Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the IAR (Illinois Assessment of Readiness) math test with fun, adaptive games. Grades 3-8. Start with a free diagnostic.",
    },
    heroTag: "Illinois · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the IAR?",
    heroSubhead: "The Illinois Assessment of Readiness is Illinois's math assessment for grades 3-8. Sports Academy Games turns IAR math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8",
    heroMeta: "Grades 3-8 · Pearson / Illinois State Board of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the IAR",
      lede: "IAR math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Ratios & Proportional Relationships",
            "Geometry",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn IAR prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact IAR math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the IAR",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–April)",
        },
        {
          title: "Why it matters",
          body: "Illinois's PARCC-lineage math test for grades 3-8; multi-step reasoning emphasis",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "IAR",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.isbe.net/iar",
  },
  {
    slug: "ilearn",
    fullName: "Indiana Learning Evaluation and Assessment Readiness Network",
    shortName: "ILEARN",
    scope: "Indiana",
    state: "Indiana",
    seo: {
      title: "ILEARN Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the ILEARN (Indiana Learning Evaluation and Assessment Readiness Network) math test with fun, adaptive games. Grades 3-8 math.…",
    },
    heroTag: "Indiana · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the ILEARN?",
    heroSubhead: "The Indiana Learning Evaluation and Assessment Readiness Network is Indiana's math assessment for grades 3-8 math. Sports Academy Games turns ILEARN math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math",
    heroMeta: "Grades 3-8 math · Cambium Assessment / Indiana DOE",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the ILEARN",
      lede: "ILEARN math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number Sense",
            "Computation",
            "Algebraic Reasoning",
            "Geometry",
            "Measurement",
            "Data Analysis",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn ILEARN prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact ILEARN math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the ILEARN",
      cards: [
        {
          title: "When it's given",
          body: "Through-year (3 Checkpoints + summative in spring)",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Indiana's through-year adaptive math assessment with checkpoints grades 3-8",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "ILEARN",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.in.gov/doe/students/assessment/ilearn/",
  },
  {
    slug: "iowa-isasp",
    fullName: "Iowa Statewide Assessment of Student Progress",
    shortName: "ISASP",
    scope: "Iowa",
    state: "Iowa",
    seo: {
      title: "ISASP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the ISASP (Iowa Statewide Assessment of Student Progress) math test with fun, adaptive games. Grades 3-11 math. Start with a free…",
    },
    heroTag: "Iowa · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the ISASP?",
    heroSubhead: "The Iowa Statewide Assessment of Student Progress is Iowa's math assessment for grades 3-11 math. Sports Academy Games turns ISASP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-11 math",
    heroMeta: "Grades 3-11 math · Pearson / Iowa Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the ISASP",
      lede: "ISASP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Measurement",
            "Data Analysis & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn ISASP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact ISASP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the ISASP",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Why it matters",
          body: "Iowa's annual spring math assessment spanning grades 3-11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "ISASP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.educateiowa.gov/pk-12/learner-readiness/assessment",
  },
  {
    slug: "kap",
    fullName: "Kansas Assessment Program",
    shortName: "KAP",
    scope: "Kansas",
    state: "Kansas",
    seo: {
      title: "KAP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the KAP (Kansas Assessment Program) math test with fun, adaptive games. Grades 3-8 + Grades 10-11. Start with a free diagnostic.",
    },
    heroTag: "Kansas · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the KAP?",
    heroSubhead: "The Kansas Assessment Program is Kansas's math assessment for grades 3-8 + grades 10-11. Sports Academy Games turns KAP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grades 10-11",
    heroMeta: "Grades 3-8 + Grades 10-11 · DRC / Kansas State Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the KAP",
      lede: "KAP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Statistics & Probability",
            "Mathematical Practices",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn KAP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact KAP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the KAP",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Kansas's standards-based math test covering grades 3-8 and high school",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "KAP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://ksassessments.org/",
  },
  {
    slug: "ksa",
    fullName: "Kentucky Summative Assessment",
    shortName: "KSA",
    scope: "Kentucky",
    state: "Kentucky",
    seo: {
      title: "KSA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the KSA (Kentucky Summative Assessment) math test with fun, adaptive games. Grades 3-8 + Grade 10. Start with a free diagnostic.",
    },
    heroTag: "Kentucky · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the KSA?",
    heroSubhead: "The Kentucky Summative Assessment is Kentucky's math assessment for grades 3-8 + grade 10. Sports Academy Games turns KSA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 10",
    heroMeta: "Grades 3-8 + Grade 10 · Pearson / Kentucky Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the KSA",
      lede: "KSA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Statistics & Probability",
            "Data Analysis",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn KSA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact KSA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the KSA",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Kentucky's annual math summative (formerly K-PREP); grades 3-8 and grade 10",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "KSA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://education.ky.gov/AA/Assessments/Pages/KentuckySummativeAssessment-.aspx",
  },
  {
    slug: "leap",
    fullName: "Louisiana Educational Assessment Program (LEAP 2025)",
    shortName: "LEAP 2025",
    scope: "Louisiana",
    state: "Louisiana",
    seo: {
      title: "LEAP 2025 Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the LEAP 2025 (Louisiana Educational Assessment Program (LEAP 2025)) math test with fun, adaptive games. Grades 3-8 + EOC…",
    },
    heroTag: "Louisiana · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the LEAP 2025?",
    heroSubhead: "The Louisiana Educational Assessment Program (LEAP 2025) is Louisiana's math assessment for grades 3-8 + eoc (algebra i/ii geometry). Sports Academy Games turns LEAP 2025 math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + EOC (Algebra I/II Geometry)",
    heroMeta: "Grades 3-8 + EOC (Algebra I/II Geometry) · Cambium Assessment / Louisiana DOE",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the LEAP 2025",
      lede: "LEAP 2025 math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn LEAP 2025 prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact LEAP 2025 math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the LEAP 2025",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Louisiana's CCSS-aligned math test — grades 3-8 plus high school EOCs",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "LEAP 2025",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://doe.louisiana.gov/school-system-leaders/measuring-results/assessment-guidance",
  },
  {
    slug: "maine-mta",
    fullName: "Maine Through-Year Assessment (NWEA)",
    shortName: "Maine MTA",
    scope: "Maine",
    state: "Maine",
    seo: {
      title: "Maine MTA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the Maine MTA (Maine Through-Year Assessment (NWEA)) math test with fun, adaptive games. Grades 3-8 + Grade 11 (2nd year HS).…",
    },
    heroTag: "Maine · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the Maine MTA?",
    heroSubhead: "The Maine Through-Year Assessment (NWEA) is Maine's math assessment for grades 3-8 + grade 11 (2nd year hs). Sports Academy Games turns Maine MTA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11 (2nd year HS)",
    heroMeta: "Grades 3-8 + Grade 11 (2nd year HS) · Maine Dept. of Education / NWEA",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the Maine MTA",
      lede: "Maine MTA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Measurement & Data",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn Maine MTA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact Maine MTA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the Maine MTA",
      cards: [
        {
          title: "When it's given",
          body: "Fall and Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (NWEA MAP-based). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Maine transitioned to NWEA through-year math assessment starting 2024-25",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "Maine MTA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://mainedoenews.net/category/assessment/",
  },
  {
    slug: "mcap",
    fullName: "Maryland Comprehensive Assessment Program",
    shortName: "MCAP",
    scope: "Maryland",
    state: "Maryland",
    seo: {
      title: "MCAP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the MCAP (Maryland Comprehensive Assessment Program) math test with fun, adaptive games. Grades 3-8 + EOC (Algebra I/Geometry).…",
    },
    heroTag: "Maryland · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the MCAP?",
    heroSubhead: "The Maryland Comprehensive Assessment Program is Maryland's math assessment for grades 3-8 + eoc (algebra i/geometry). Sports Academy Games turns MCAP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + EOC (Algebra I/Geometry)",
    heroMeta: "Grades 3-8 + EOC (Algebra I/Geometry) · Pearson / Maryland State Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the MCAP",
      lede: "MCAP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Ratios & Proportional Relationships",
            "Number System",
            "Expressions & Equations",
            "Geometry",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn MCAP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact MCAP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the MCAP",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Maryland's annual math accountability test — grades 3-8 plus HS EOC courses",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "MCAP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://marylandpublicschools.org/programs/pages/assessment/mcap.aspx",
  },
  {
    slug: "mcas",
    fullName: "Massachusetts Comprehensive Assessment System",
    shortName: "MCAS",
    scope: "Massachusetts",
    state: "Massachusetts",
    seo: {
      title: "MCAS Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the MCAS (Massachusetts Comprehensive Assessment System) math test with fun, adaptive games. Grades 3-8 + Grade 10. Start with a…",
    },
    heroTag: "Massachusetts · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the MCAS?",
    heroSubhead: "The Massachusetts Comprehensive Assessment System is Massachusetts's math assessment for grades 3-8 + grade 10. Sports Academy Games turns MCAS math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 10",
    heroMeta: "Grades 3-8 + Grade 10 · DESE / Cognia",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the MCAS",
      lede: "MCAS math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Quantity",
            "Algebra & Functions",
            "Statistics & Probability",
            "Geometry",
            "Integrated Math",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn MCAS prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact MCAS math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the MCAS",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–May)",
        },
        {
          title: "Why it matters",
          body: "High-stakes MA math test — grade 10 required for graduation",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "MCAS",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.doe.mass.edu/mcas/",
  },
  {
    slug: "m-step",
    fullName: "Michigan Student Test of Educational Progress",
    shortName: "M-STEP",
    scope: "Michigan",
    state: "Michigan",
    seo: {
      title: "M-STEP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the M-STEP (Michigan Student Test of Educational Progress) math test with fun, adaptive games. Grades 3-7 math (G8 PSAT 8/9) +…",
    },
    heroTag: "Michigan · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the M-STEP?",
    heroSubhead: "The Michigan Student Test of Educational Progress is Michigan's math assessment for grades 3-7 math (g8 psat 8/9) + psat/sat hs. Sports Academy Games turns M-STEP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-7 math (G8 PSAT 8/9) + PSAT/SAT HS",
    heroMeta: "Grades 3-7 math (G8 PSAT 8/9) + PSAT/SAT HS · Michigan Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the M-STEP",
      lede: "M-STEP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Measurement & Data",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn M-STEP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact M-STEP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the M-STEP",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT Smarter Balanced–based). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Michigan uses Smarter Balanced for grades 3-7; PSAT and SAT for grade 8 and HS",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "M-STEP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.michigan.gov/mde/services/student-assessment/m-step",
  },
  {
    slug: "mca",
    fullName: "Minnesota Comprehensive Assessment",
    shortName: "MCA",
    scope: "Minnesota",
    state: "Minnesota",
    seo: {
      title: "MCA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the MCA (Minnesota Comprehensive Assessment) math test with fun, adaptive games. Grades 3-8 + Grade 11. Start with a free…",
    },
    heroTag: "Minnesota · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the MCA?",
    heroSubhead: "The Minnesota Comprehensive Assessment is Minnesota's math assessment for grades 3-8 + grade 11. Sports Academy Games turns MCA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · Pearson / Minnesota Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the MCA",
      lede: "MCA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebra",
            "Geometry & Measurement",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn MCA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact MCA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the MCA",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–May)",
        },
        {
          title: "Why it matters",
          body: "Minnesota's primary math accountability test — grades 3-8 and grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "MCA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://education.mn.gov/MDE/dse/test/mca/",
  },
  {
    slug: "maap",
    fullName: "Mississippi Academic Assessment Program",
    shortName: "MAAP",
    scope: "Mississippi",
    state: "Mississippi",
    seo: {
      title: "MAAP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the MAAP (Mississippi Academic Assessment Program) math test with fun, adaptive games. Grades 3-8 + EOC (Algebra I/Geometry).…",
    },
    heroTag: "Mississippi · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the MAAP?",
    heroSubhead: "The Mississippi Academic Assessment Program is Mississippi's math assessment for grades 3-8 + eoc (algebra i/geometry). Sports Academy Games turns MAAP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + EOC (Algebra I/Geometry)",
    heroMeta: "Grades 3-8 + EOC (Algebra I/Geometry) · DRC / Mississippi Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the MAAP",
      lede: "MAAP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Statistics & Probability",
            "Data Analysis",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn MAAP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact MAAP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the MAAP",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Mississippi's annual math summative — grades 3-8 plus Algebra I and English II EOC",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "MAAP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.mdek12.org/OSA/assessment",
  },
  {
    slug: "mo-map",
    fullName: "Missouri Assessment Program",
    shortName: "MO MAP",
    scope: "Missouri",
    state: "Missouri",
    seo: {
      title: "MO MAP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the MO MAP (Missouri Assessment Program) math test with fun, adaptive games. Grades 3-8 + EOC (Algebra I/Geometry). Start with a…",
    },
    heroTag: "Missouri · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the MO MAP?",
    heroSubhead: "The Missouri Assessment Program is Missouri's math assessment for grades 3-8 + eoc (algebra i/geometry). Sports Academy Games turns MO MAP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + EOC (Algebra I/Geometry)",
    heroMeta: "Grades 3-8 + EOC (Algebra I/Geometry) · Pearson / Missouri Dept. of Elementary & Secondary Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the MO MAP",
      lede: "MO MAP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Expressions & Equations",
            "Geometry",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn MO MAP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact MO MAP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the MO MAP",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Missouri's primary math assessment — grades 3-8 and end-of-course exams",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "MO MAP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://dese.mo.gov/governmental-affairs/assessment",
  },
  {
    slug: "mast",
    fullName: "Montana Aligned to Standards Through-Year Assessment",
    shortName: "MAST",
    scope: "Montana",
    state: "Montana",
    seo: {
      title: "MAST Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the MAST (Montana Aligned to Standards Through-Year Assessment) math test with fun, adaptive games. Grades 3-8 (+ ACT for Grade…",
    },
    heroTag: "Montana · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the MAST?",
    heroSubhead: "The Montana Aligned to Standards Through-Year Assessment is Montana's math assessment for grades 3-8 (+ act for grade 11). Sports Academy Games turns MAST math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 (+ ACT for Grade 11)",
    heroMeta: "Grades 3-8 (+ ACT for Grade 11) · Montana Office of Public Instruction",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the MAST",
      lede: "MAST math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry & Measurement",
            "Statistics & Probability",
            "Functions",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn MAST prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact MAST math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the MAST",
      cards: [
        {
          title: "When it's given",
          body: "Through-year (fall + spring windows)",
        },
        {
          title: "Why it matters",
          body: "Montana's 2024-launch replacement for SBAC — through-year math grades 3-8",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "MAST",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://opi.mt.gov/Leadership/Assessment-Accountability/MontCAS",
  },
  {
    slug: "nesa",
    fullName: "Nebraska Student Assessment (NeSA)",
    shortName: "NeSA",
    scope: "Nebraska",
    state: "Nebraska",
    seo: {
      title: "NeSA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the NeSA (Nebraska Student Assessment (NeSA)) math test with fun, adaptive games. Grades 3-8 + Grade 11 (ACT). Start with a free…",
    },
    heroTag: "Nebraska · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the NeSA?",
    heroSubhead: "The Nebraska Student Assessment (NeSA) is Nebraska's math assessment for grades 3-8 + grade 11 (act). Sports Academy Games turns NeSA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11 (ACT)",
    heroMeta: "Grades 3-8 + Grade 11 (ACT) · Cognia / Nebraska Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the NeSA",
      lede: "NeSA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number Sense",
            "Algebraic Thinking",
            "Geometry",
            "Measurement",
            "Data Analysis",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn NeSA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact NeSA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the NeSA",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Nebraska's annual math accountability test for grades 3-8 with ACT at grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "NeSA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.education.ne.gov/assessment/",
  },
  {
    slug: "nevada-sbac",
    fullName: "Nevada Smarter Balanced Assessment",
    shortName: "Nevada SBAC",
    scope: "Nevada",
    state: "Nevada",
    seo: {
      title: "Nevada SBAC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the Nevada SBAC (Nevada Smarter Balanced Assessment) math test with fun, adaptive games. Grades 3-8 + Grade 11. Start with a free…",
    },
    heroTag: "Nevada · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the Nevada SBAC?",
    heroSubhead: "The Nevada Smarter Balanced Assessment is Nevada's math assessment for grades 3-8 + grade 11. Sports Academy Games turns Nevada SBAC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · Nevada Dept. of Education / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the Nevada SBAC",
      lede: "Nevada SBAC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn Nevada SBAC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact Nevada SBAC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the Nevada SBAC",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Nevada uses SBAC for annual math accountability — grades 3-8 and grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "Nevada SBAC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://doe.nv.gov/News__Media/Press_Releases/2025/Nevada_Students_Make_Gains_on_Smarter_Balanced_Assessment/",
  },
  {
    slug: "nh-sas",
    fullName: "New Hampshire Statewide Assessment System",
    shortName: "NH SAS",
    scope: "New Hampshire",
    state: "New Hampshire",
    seo: {
      title: "NH SAS Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the NH SAS (New Hampshire Statewide Assessment System) math test with fun, adaptive games. Grades 3-8 + Grade 11 (SAT). Start…",
    },
    heroTag: "New Hampshire · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the NH SAS?",
    heroSubhead: "The New Hampshire Statewide Assessment System is New Hampshire's math assessment for grades 3-8 + grade 11 (sat). Sports Academy Games turns NH SAS math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11 (SAT)",
    heroMeta: "Grades 3-8 + Grade 11 (SAT) · Cognia / NHDOE",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the NH SAS",
      lede: "NH SAS math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Expressions & Equations",
            "Geometry",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn NH SAS prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact NH SAS math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the NH SAS",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Why it matters",
          body: "New Hampshire's spring math assessment — grades 3-8 using state test; SAT grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "NH SAS",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.education.nh.gov/accountability/assessments",
  },
  {
    slug: "njsla",
    fullName: "New Jersey Student Learning Assessments",
    shortName: "NJSLA",
    scope: "New Jersey",
    state: "New Jersey",
    seo: {
      title: "NJSLA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the NJSLA (New Jersey Student Learning Assessments) math test with fun, adaptive games. Grades 3-9 + EOC (Algebra…",
    },
    heroTag: "New Jersey · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the NJSLA?",
    heroSubhead: "The New Jersey Student Learning Assessments is New Jersey's math assessment for grades 3-9 + eoc (algebra i/geometry/algebra ii). Sports Academy Games turns NJSLA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-9 + EOC (Algebra I/Geometry/Algebra II)",
    heroMeta: "Grades 3-9 + EOC (Algebra I/Geometry/Algebra II) · New Jersey Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the NJSLA",
      lede: "NJSLA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Ratios & Proportional Relationships",
            "Geometry",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn NJSLA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact NJSLA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the NJSLA",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Adaptive test",
          body: "Yes (adaptive 2025+). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "New Jersey's PARCC-successor math test — grades 3-9 plus HS course EOCs",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "NJSLA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.nj.gov/education/assessment/",
  },
  {
    slug: "nm-mssa",
    fullName: "New Mexico Measures of Student Success and Achievement",
    shortName: "NM-MSSA",
    scope: "New Mexico",
    state: "New Mexico",
    seo: {
      title: "NM-MSSA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the NM-MSSA (New Mexico Measures of Student Success and Achievement) math test with fun, adaptive games. Grades 3-8 (+ SAT for…",
    },
    heroTag: "New Mexico · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the NM-MSSA?",
    heroSubhead: "The New Mexico Measures of Student Success and Achievement is New Mexico's math assessment for grades 3-8 (+ sat for grade 11). Sports Academy Games turns NM-MSSA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 (+ SAT for Grade 11)",
    heroMeta: "Grades 3-8 (+ SAT for Grade 11) · Cognia / New Mexico PED",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the NM-MSSA",
      lede: "NM-MSSA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Statistics & Probability",
            "Functions",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn NM-MSSA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact NM-MSSA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the NM-MSSA",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "NM's annual math test for grades 3-8; SAT used as HS accountability measure",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "NM-MSSA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://web.ped.nm.gov/bureaus/assessment/mssa-resources/",
  },
  {
    slug: "ny-state-math",
    fullName: "New York State Grades 3-8 Math Tests + Regents",
    shortName: "NY State Math",
    scope: "New York",
    state: "New York",
    seo: {
      title: "NY State Math Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the NY State Math (New York State Grades 3-8 Math Tests + Regents) math test with fun, adaptive games. Grades 3-8 + Regents…",
    },
    heroTag: "New York · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the NY State Math?",
    heroSubhead: "The New York State Grades 3-8 Math Tests + Regents is New York's math assessment for grades 3-8 + regents (algebra i/geometry/algebra ii). Sports Academy Games turns NY State Math math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Regents (Algebra I/Geometry/Algebra II)",
    heroMeta: "Grades 3-8 + Regents (Algebra I/Geometry/Algebra II) · NYSED / Questar",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the NY State Math",
      lede: "NY State Math math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Ratios & Proportional Relationships",
            "Geometry",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn NY State Math prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact NY State Math math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the NY State Math",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May) for grades 3-8; June for Regents",
        },
        {
          title: "Why it matters",
          body: "NY's rigorous annual math exams — grades 3-8 + Regents EOC for HS credit",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "NY State Math",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.nysed.gov/state-assessment/grades-3-8-ela-and-math-test-manuals",
  },
  {
    slug: "nc-eog-eoc",
    fullName: "North Carolina End-of-Grade and End-of-Course Math Tests",
    shortName: "NC EOG/EOC",
    scope: "North Carolina",
    state: "North Carolina",
    seo: {
      title: "NC EOG/EOC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the NC EOG/EOC (North Carolina End-of-Grade and End-of-Course Math Tests) math test with fun, adaptive games. Grades 3-8 EOG + HS…",
    },
    heroTag: "North Carolina · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the NC EOG/EOC?",
    heroSubhead: "The North Carolina End-of-Grade and End-of-Course Math Tests is North Carolina's math assessment for grades 3-8 eog + hs eoc (math 1/2/3). Sports Academy Games turns NC EOG/EOC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 EOG + HS EOC (Math 1/2/3)",
    heroMeta: "Grades 3-8 EOG + HS EOC (Math 1/2/3) · DPI NC / Questar",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the NC EOG/EOC",
      lede: "NC EOG/EOC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Functions",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn NC EOG/EOC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact NC EOG/EOC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the NC EOG/EOC",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "NC's annual math tests — EOG grades 3-8 plus Math 1/2/3 EOC for high school",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "NC EOG/EOC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.dpi.nc.gov/districts-schools/accountability-and-testing/state-tests/end-grade-eog",
  },
  {
    slug: "ndsa",
    fullName: "North Dakota State Assessment",
    shortName: "NDSA",
    scope: "North Dakota",
    state: "North Dakota",
    seo: {
      title: "NDSA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the NDSA (North Dakota State Assessment) math test with fun, adaptive games. Grades 3-8 + Grade 11. Start with a free diagnostic.",
    },
    heroTag: "North Dakota · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the NDSA?",
    heroSubhead: "The North Dakota State Assessment is North Dakota's math assessment for grades 3-8 + grade 11. Sports Academy Games turns NDSA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · Cognia / North Dakota Dept. of Public Instruction",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the NDSA",
      lede: "NDSA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Statistics & Probability",
            "Measurement",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn NDSA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact NDSA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the NDSA",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "North Dakota's spring math accountability exam for grades 3-8 and grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "NDSA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.nd.gov/dpi/districtsadmin/testing/stateassess",
  },
  {
    slug: "ohio-tests",
    fullName: "Ohio State Tests (Math)",
    shortName: "Ohio State Tests",
    scope: "Ohio",
    state: "Ohio",
    seo: {
      title: "Ohio State Tests Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the Ohio State Tests (Math) math test with fun, adaptive games. Grades 3-8 + EOC (Algebra I/Geometry). Start with a free…",
    },
    heroTag: "Ohio · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the Ohio State Tests?",
    heroSubhead: "The Ohio State Tests (Math) is Ohio's math assessment for grades 3-8 + eoc (algebra i/geometry). Sports Academy Games turns Ohio State Tests math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + EOC (Algebra I/Geometry)",
    heroMeta: "Grades 3-8 + EOC (Algebra I/Geometry) · DRC / Ohio Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the Ohio State Tests",
      lede: "Ohio State Tests math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Functions",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn Ohio State Tests prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact Ohio State Tests math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the Ohio State Tests",
      cards: [
        {
          title: "When it's given",
          body: "Spring + Fall (EOC); Fall for Grade 3 ELA",
        },
        {
          title: "Why it matters",
          body: "Ohio's annual math tests — grades 3-8 plus Algebra I and Geometry EOC for HS",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "Ohio State Tests",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://education.ohio.gov/Topics/Testing/Ohios-State-Test-in-ELA-Math-Science-SocialStudies",
  },
  {
    slug: "ostp",
    fullName: "Oklahoma School Testing Program",
    shortName: "OSTP",
    scope: "Oklahoma",
    state: "Oklahoma",
    seo: {
      title: "OSTP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the OSTP (Oklahoma School Testing Program) math test with fun, adaptive games. Grades 3-8 + Grade 11 (EOI). Start with a free…",
    },
    heroTag: "Oklahoma · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the OSTP?",
    heroSubhead: "The Oklahoma School Testing Program is Oklahoma's math assessment for grades 3-8 + grade 11 (eoi). Sports Academy Games turns OSTP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11 (EOI)",
    heroMeta: "Grades 3-8 + Grade 11 (EOI) · Cognia / Oklahoma State Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the OSTP",
      lede: "OSTP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Measurement & Data",
            "Functions",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn OSTP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact OSTP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the OSTP",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Why it matters",
          body: "Oklahoma's standards-based math test — grades 3-8 and grade 11 EOI",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "OSTP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://oklahoma.gov/education/services/assessments/state-testing-resources.html",
  },
  {
    slug: "oaks",
    fullName: "Oregon Assessment of Knowledge and Skills (Smarter Balanced)",
    shortName: "OR SBAC",
    scope: "Oregon",
    state: "Oregon",
    seo: {
      title: "OR SBAC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the OR SBAC (Oregon Assessment of Knowledge and Skills (Smarter Balanced)) math test with fun, adaptive games. Grades 3-8 + Grade…",
    },
    heroTag: "Oregon · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the OR SBAC?",
    heroSubhead: "The Oregon Assessment of Knowledge and Skills (Smarter Balanced) is Oregon's math assessment for grades 3-8 + grade 11. Sports Academy Games turns OR SBAC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · Oregon Dept. of Education / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the OR SBAC",
      lede: "OR SBAC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn OR SBAC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact OR SBAC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the OR SBAC",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Oregon uses SBAC as its state math accountability measure — grades 3-8 and 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "OR SBAC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.oregon.gov/ode/educator-resources/assessment/pages/assessment-group-reports.aspx",
  },
  {
    slug: "pssa",
    fullName: "Pennsylvania System of School Assessment",
    shortName: "PSSA",
    scope: "Pennsylvania",
    state: "Pennsylvania",
    seo: {
      title: "PSSA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the PSSA (Pennsylvania System of School Assessment) math test with fun, adaptive games. Grades 3-8 math. Start with a free…",
    },
    heroTag: "Pennsylvania · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the PSSA?",
    heroSubhead: "The Pennsylvania System of School Assessment is Pennsylvania's math assessment for grades 3-8 math. Sports Academy Games turns PSSA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math",
    heroMeta: "Grades 3-8 math · DRC / Pennsylvania Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the PSSA",
      lede: "PSSA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Measurement",
            "Data Analysis",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn PSSA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact PSSA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the PSSA",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April)",
        },
        {
          title: "Why it matters",
          body: "Pennsylvania's major math test — grades 3-8; Keystone Algebra I for HS",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "PSSA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.pa.gov/agencies/education/programs-and-services/instruction/elementary-and-secondary-education/assessment-and-accountability/pennsylvania-system-of-school-assessment-pssa",
  },
  {
    slug: "ricas",
    fullName: "Rhode Island Comprehensive Assessment System",
    shortName: "RICAS",
    scope: "Rhode Island",
    state: "Rhode Island",
    seo: {
      title: "RICAS Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the RICAS (Rhode Island Comprehensive Assessment System) math test with fun, adaptive games. Grades 3-8 math. Start with a free…",
    },
    heroTag: "Rhode Island · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the RICAS?",
    heroSubhead: "The Rhode Island Comprehensive Assessment System is Rhode Island's math assessment for grades 3-8 math. Sports Academy Games turns RICAS math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math",
    heroMeta: "Grades 3-8 math · Cognia / RIDE",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the RICAS",
      lede: "RICAS math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Operations & Algebraic Thinking",
            "Number & Operations",
            "Ratios & Proportional Relationships",
            "Geometry",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn RICAS prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact RICAS math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the RICAS",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–May)",
        },
        {
          title: "Why it matters",
          body: "Rhode Island's annual math test for grades 3-8; PSAT/SAT for HS",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "RICAS",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://ride.ri.gov/instruction-assessment/assessment/assessment-results",
  },
  {
    slug: "sc-ready",
    fullName: "South Carolina College- and Career-Ready Assessment",
    shortName: "SC READY",
    scope: "South Carolina",
    state: "South Carolina",
    seo: {
      title: "SC READY Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the SC READY (South Carolina College- and Career-Ready Assessment) math test with fun, adaptive games. Grades 3-8 math + SCPASS…",
    },
    heroTag: "South Carolina · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the SC READY?",
    heroSubhead: "The South Carolina College- and Career-Ready Assessment is South Carolina's math assessment for grades 3-8 math + scpass eoc. Sports Academy Games turns SC READY math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math + SCPASS EOC",
    heroMeta: "Grades 3-8 math + SCPASS EOC · Cambium Assessment / South Carolina DOE",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the SC READY",
      lede: "SC READY math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Measurement",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn SC READY prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact SC READY math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the SC READY",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "South Carolina's annual math summative for grades 3-8",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "SC READY",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.ed.sc.gov/tests/middle/sc-ready/",
  },
  {
    slug: "sd-sbac",
    fullName: "South Dakota ELA and Math Assessments (Smarter Balanced)",
    shortName: "SD SBAC",
    scope: "South Dakota",
    state: "South Dakota",
    seo: {
      title: "SD SBAC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the SD SBAC (South Dakota ELA and Math Assessments (Smarter Balanced)) math test with fun, adaptive games. Grades 3-8 math. Start…",
    },
    heroTag: "South Dakota · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the SD SBAC?",
    heroSubhead: "The South Dakota ELA and Math Assessments (Smarter Balanced) is South Dakota's math assessment for grades 3-8 math. Sports Academy Games turns SD SBAC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math",
    heroMeta: "Grades 3-8 math · South Dakota DOE / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the SD SBAC",
      lede: "SD SBAC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn SD SBAC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact SD SBAC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the SD SBAC",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–May)",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "South Dakota uses SBAC as its K-8 math accountability test",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "SD SBAC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://doe.sd.gov/assessment/",
  },
  {
    slug: "tcap",
    fullName: "Tennessee Comprehensive Assessment Program",
    shortName: "TCAP",
    scope: "Tennessee",
    state: "Tennessee",
    seo: {
      title: "TCAP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the TCAP (Tennessee Comprehensive Assessment Program) math test with fun, adaptive games. Grades 2-8 + EOC (Algebra I/II…",
    },
    heroTag: "Tennessee · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the TCAP?",
    heroSubhead: "The Tennessee Comprehensive Assessment Program is Tennessee's math assessment for grades 2-8 + eoc (algebra i/ii geometry). Sports Academy Games turns TCAP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 2-8 + EOC (Algebra I/II Geometry)",
    heroMeta: "Grades 2-8 + EOC (Algebra I/II Geometry) · Pearson / Tennessee DOE (TNReady brand)",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the TCAP",
      lede: "TCAP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Measurement",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn TCAP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact TCAP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the TCAP",
      cards: [
        {
          title: "When it's given",
          body: "Spring (April–May)",
        },
        {
          title: "Why it matters",
          body: "Tennessee's flagship math summative — grades 2-8 plus HS EOC courses",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "TCAP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.tn.gov/education/districts/lea-operations/assessment/tcap-blueprints.html",
  },
  {
    slug: "ut-rise",
    fullName: "Utah Readiness Improvement Success Empowerment",
    shortName: "RISE",
    scope: "Utah",
    state: "Utah",
    seo: {
      title: "RISE Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the RISE (Utah Readiness Improvement Success Empowerment) math test with fun, adaptive games. Grades 3-8 math. Start with a free…",
    },
    heroTag: "Utah · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the RISE?",
    heroSubhead: "The Utah Readiness Improvement Success Empowerment is Utah's math assessment for grades 3-8 math. Sports Academy Games turns RISE math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math",
    heroMeta: "Grades 3-8 math · Cognia / Utah State Board of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the RISE",
      lede: "RISE math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Measurement & Data",
            "Statistics",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn RISE prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact RISE math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the RISE",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Utah's adaptive summative math test for grades 3-8",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "RISE",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://schools.utah.gov/assessment/assessments.php",
  },
  {
    slug: "vt-sbac",
    fullName: "Vermont Smarter Balanced Assessment",
    shortName: "VT SBAC",
    scope: "Vermont",
    state: "Vermont",
    seo: {
      title: "VT SBAC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the VT SBAC (Vermont Smarter Balanced Assessment) math test with fun, adaptive games. Grades 3-8 + Grade 11. Start with a free…",
    },
    heroTag: "Vermont · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the VT SBAC?",
    heroSubhead: "The Vermont Smarter Balanced Assessment is Vermont's math assessment for grades 3-8 + grade 11. Sports Academy Games turns VT SBAC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · Vermont AOE / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the VT SBAC",
      lede: "VT SBAC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn VT SBAC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact VT SBAC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the VT SBAC",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Vermont uses SBAC as its state math accountability measure",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "VT SBAC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://education.vermont.gov/student-learning/assessment",
  },
  {
    slug: "sol",
    fullName: "Virginia Standards of Learning",
    shortName: "SOL",
    scope: "Virginia",
    state: "Virginia",
    seo: {
      title: "SOL Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the SOL (Virginia Standards of Learning) math test with fun, adaptive games. Grades 3-8 + EOC (Algebra I/Geometry/Algebra II).…",
    },
    heroTag: "Virginia · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the SOL?",
    heroSubhead: "The Virginia Standards of Learning is Virginia's math assessment for grades 3-8 + eoc (algebra i/geometry/algebra ii). Sports Academy Games turns SOL math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + EOC (Algebra I/Geometry/Algebra II)",
    heroMeta: "Grades 3-8 + EOC (Algebra I/Geometry/Algebra II) · Pearson / Virginia DOE",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the SOL",
      lede: "SOL math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Measurement & Geometry",
            "Probability & Statistics",
            "Patterns & Algebra",
            "Computational Thinking",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn SOL prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact SOL math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the SOL",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Virginia's accountability math tests — grades 3-8 plus EOC Algebra I/Geometry/Algebra II",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "SOL",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://www.doe.virginia.gov/assessment-accountability/assessment/state-assessments",
  },
  {
    slug: "wa-sbac",
    fullName: "Washington Smarter Balanced Assessment",
    shortName: "WA SBAC",
    scope: "Washington",
    state: "Washington",
    seo: {
      title: "WA SBAC Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the WA SBAC (Washington Smarter Balanced Assessment) math test with fun, adaptive games. Grades 3-8 + Grade 11. Start with a free…",
    },
    heroTag: "Washington · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the WA SBAC?",
    heroSubhead: "The Washington Smarter Balanced Assessment is Washington's math assessment for grades 3-8 + grade 11. Sports Academy Games turns WA SBAC math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + Grade 11",
    heroMeta: "Grades 3-8 + Grade 11 · OSPI / Smarter Balanced",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the WA SBAC",
      lede: "WA SBAC math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Concepts & Procedures",
            "Problem-Solving & Modeling",
            "Communicating Reasoning",
            "Number Sense",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn WA SBAC prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact WA SBAC math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the WA SBAC",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT + performance task). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "Washington State uses SBAC for its annual math accountability test",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "WA SBAC",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://ospi.k12.wa.us/student-success/testing/smarter-balanced-assessment",
  },
  {
    slug: "wvgsa",
    fullName: "West Virginia General Summative Assessment",
    shortName: "WVGSA",
    scope: "West Virginia",
    state: "West Virginia",
    seo: {
      title: "WVGSA Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the WVGSA (West Virginia General Summative Assessment) math test with fun, adaptive games. Grades 3-8 + SAT Grade 11. Start with…",
    },
    heroTag: "West Virginia · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the WVGSA?",
    heroSubhead: "The West Virginia General Summative Assessment is West Virginia's math assessment for grades 3-8 + SAT Grade 11. Sports Academy Games turns WVGSA math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 + SAT Grade 11",
    heroMeta: "Grades 3-8 + SAT Grade 11 · Cognia / West Virginia Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the WVGSA",
      lede: "WVGSA math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry",
            "Measurement",
            "Data Analysis",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn WVGSA prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact WVGSA math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the WVGSA",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Adaptive test",
          body: "Yes (CAT). Our practice adapts too — difficulty meets your child where they are.",
        },
        {
          title: "Why it matters",
          body: "WV's adaptive math test for grades 3-8; SAT administered statewide for grade 11",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "WVGSA",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://wvde.us/assessment/",
  },
  {
    slug: "forward",
    fullName: "Wisconsin Forward Exam",
    shortName: "Forward",
    scope: "Wisconsin",
    state: "Wisconsin",
    seo: {
      title: "Forward Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the Forward (Wisconsin Forward Exam) math test with fun, adaptive games. Grades 3-8 math. Start with a free diagnostic.",
    },
    heroTag: "Wisconsin · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the Forward?",
    heroSubhead: "The Wisconsin Forward Exam is Wisconsin's math assessment for grades 3-8 math. Sports Academy Games turns Forward math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-8 math",
    heroMeta: "Grades 3-8 math · DRC / Wisconsin DPI",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the Forward",
      lede: "Forward math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Thinking",
            "Geometry",
            "Measurement & Data",
            "Statistics & Probability",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn Forward prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact Forward math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the Forward",
      cards: [
        {
          title: "When it's given",
          body: "Spring (March–April)",
        },
        {
          title: "Why it matters",
          body: "Wisconsin's spring math exam for grades 3-8; ACT Aspire/SAT for HS",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "Forward",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://dpi.wi.gov/assessment/forward",
  },
  {
    slug: "wy-topp",
    fullName: "Wyoming Test of Proficiency and Progress",
    shortName: "WY-TOPP",
    scope: "Wyoming",
    state: "Wyoming",
    seo: {
      title: "WY-TOPP Math Prep for Kids | Sports Academy Games",
      description: "Help your child prepare for the WY-TOPP (Wyoming Test of Proficiency and Progress) math test with fun, adaptive games. Grades 3-10 math. Start with a free…",
    },
    heroTag: "Wyoming · Math Test Prep",
    heroHeadline: "Does your child need help preparing for the WY-TOPP?",
    heroSubhead: "The Wyoming Test of Proficiency and Progress is Wyoming's math assessment for grades 3-10 math. Sports Academy Games turns WY-TOPP math practice into fast, fun games that build real mastery — not test anxiety.",
    gradeRange: "Grades 3-10 math",
    heroMeta: "Grades 3-10 math · DRC / Wyoming Dept. of Education",
    topics: {
      eyebrow: "What the test covers",
      title: "The math on the WY-TOPP",
      lede: "WY-TOPP math focuses on these areas. Our games drill every one of them.",
      groups: [
        {
          label: "Math domains",
          items: [
            "Number & Operations",
            "Algebraic Reasoning",
            "Geometry & Measurement",
            "Statistics & Probability",
            "Functions",
          ],
        },
      ],
    },
    help: {
      eyebrow: "How we help",
      title: "Turn WY-TOPP prep into play",
      points: [
        "A free adaptive diagnostic pinpoints exactly which math skills your child has and hasn't mastered.",
        "Short, game-based drills target the exact WY-TOPP math domains — 60-90 seconds of focused practice at a time.",
        "Mastery tracking shows real progress over time, so practice stays aligned to where your child actually needs it.",
      ],
    },
    flex: {
      eyebrow: "About the test",
      title: "Know the WY-TOPP",
      cards: [
        {
          title: "When it's given",
          body: "Spring",
        },
        {
          title: "Why it matters",
          body: "Wyoming's annual K-10 math assessment aligned to Wyoming Content Standards",
        },
      ],
    },
    cta: {
      eyebrow: "Start free",
      title: "See where your child stands",
      titleStripe: "WY-TOPP",
      sub: "Take a free adaptive diagnostic — no signup, no card.",
      label: "Start the free diagnostic",
      to: "/diagnostic",
    },
    sourceUrl: "https://edu.wyoming.gov/transparency/assessment/",
  },
];
